import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    AbstractControl,
    FormControlStatus,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { IAnnualReportTemplateDTO } from '@modeso/aumico-lib-annualreport-fe';
import {
    EMPTY_ARRAY_LENGTH,
    IAumicoDragDropItem,
    USER_INPUT_DEBOUNCE_TIME,
    validateNoEmptyField,
} from '@modeso/aumico-lib-core-fe';
import {
    AnnualReportStep,
    AnnualReportTemplateConsistency,
    AnnualReportTemplateState,
} from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { localizationKeys } from '../../../constants/localizationKeys';
import { AnnualReportBusinessModel } from '../../../enums/annual-report-business-model.enum';

const SWITZERLAND_ID_CODE = 'CH';
const GENERAL_INFORMATION_KEY = localizationKeys.aumico_reports_annualreport_navigation_generalinformation;
const IMPORT_BALANCE_INCOME_KEY = localizationKeys.aumico_reports_annualreport_navigation_import;
const FINANCIAL_CLOSING_PROCESS_KEY = localizationKeys.aumico_reports_annualreport_navigation_fcp;
const BALANCE_ACCOUNT_PROOF_KEY = localizationKeys.aumico_reports_annualreport_navigation_balance_account_proof;
const INCOME_ACCOUNT_PROOF_KEY = localizationKeys.aumico_reports_annualreport_navigation_income_account_proof;
const CFS_KEY = localizationKeys.aumico_reports_annualreport_navigation_cfs;
const NOTES_KEY = localizationKeys.aumico_reports_annualreport_navigation_notes;
const AOP_KEY = localizationKeys.aumico_reports_annualreport_navigation_aop;
const EMS_KEY = localizationKeys.aumico_reports_annualreport_navigation_equity_movement_schedule;
const ADJUSTMENTS_RECLASSIFICATION_KEY =
    localizationKeys.aumico_reports_annualreport_navigation_adjustments_and_reclasses;
const CR_KEY = localizationKeys.aumico_reports_annualreport_navigation_complementary_reports;
const PRINT_KEY = localizationKeys.aumico_reports_annualreport_navigation_finish_and_print;

@UntilDestroy()
@Component({
    selector: 'aumico-ar-template-general-information-form-page',
    templateUrl: 'ar-template-general-information-form.page.html',
    styleUrls: ['ar-template-general-information-form.page.scss'],
})
export class ArTemplateGeneralInformationFormPageComponent implements OnInit, OnChanges {
    public formGroup: UntypedFormGroup | undefined;
    private defaultCountry: INameId;
    public stateOptions: INameId[] = [];
    public consistencyOptions: INameId[] = [];
    public businessModelsOptions: INameId[] = [];

    public defaultAnnualReportSteps: IAumicoDragDropItem[] = [
        {
            id: AnnualReportStep.GENERAL_INFORMATION,
            descriptionKey: GENERAL_INFORMATION_KEY,
            fixed: true,
        },
        {
            id: AnnualReportStep.IMPORT_BALANCE_INCOME,
            descriptionKey: IMPORT_BALANCE_INCOME_KEY,
        },
        {
            id: AnnualReportStep.FINANCIAL_CLOSING_PROCESS,
            descriptionKey: FINANCIAL_CLOSING_PROCESS_KEY,
        },
        {
            id: AnnualReportStep.BALANCE_ACCOUNT_PROOF,
            descriptionKey: BALANCE_ACCOUNT_PROOF_KEY,
        },
        {
            id: AnnualReportStep.INCOME_ACCOUNT_PROOF,
            descriptionKey: INCOME_ACCOUNT_PROOF_KEY,
        },
        {
            id: AnnualReportStep.CFS,
            descriptionKey: CFS_KEY,
        },
        {
            id: AnnualReportStep.NOTES,
            descriptionKey: NOTES_KEY,
        },
        {
            id: AnnualReportStep.AOP,
            descriptionKey: AOP_KEY,
        },
        {
            id: AnnualReportStep.EMS,
            descriptionKey: EMS_KEY,
        },
        {
            id: AnnualReportStep.ADJUSTMENTS_RECLASSIFICATION,
            descriptionKey: ADJUSTMENTS_RECLASSIFICATION_KEY,
        },
        {
            id: AnnualReportStep.CR,
            descriptionKey: CR_KEY,
        },
        {
            id: AnnualReportStep.PRINT,
            descriptionKey: PRINT_KEY,
            fixed: true,
        },
    ];

    private annualReportSteps: AnnualReportStep[] = [];
    public annualReportSavedDragAndDropItems: IAumicoDragDropItem[] = [];
    private arTemplateToBeSaved: IAnnualReportTemplateDTO | undefined;

    @Input() currentArTemplate: IAnnualReportTemplateDTO | undefined;
    @Input() accountingBasesOptions: INameId[] = [];
    @Input() legalFormsOptions: INameId[] = [];
    @Input() localesOptions: INameId[] = [];

    public countries: INameId[] = [];
    @Input() set countriesOptions(countries: INameId[]) {
        this.countries = [...countries];
        this.defaultCountry = countries.find((country) => country.id === SWITZERLAND_ID_CODE);
    }

    @Output() generalInformationChanged: EventEmitter<IAnnualReportTemplateDTO> =
        new EventEmitter<IAnnualReportTemplateDTO>();
    @Output() formStatusChanged: EventEmitter<FormControlStatus> = new EventEmitter<FormControlStatus>();

    constructor(private readonly i18NextService: I18NextService) {
        this.handleStateOptions();
        this.handleConsistencyOptions();
        this.handleBusinessModelsOptions();
        this.initializeForm();
    }

    ngOnInit(): void {
        this.monitorFormChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['accountingBasesOptions'] != null &&
            this.accountingBasesOptions.length > EMPTY_ARRAY_LENGTH &&
            this.currentArTemplate != null
        ) {
            this.setSavedAccountingBasesValue();
        }
        if (
            changes['legalFormsOptions'] != null &&
            this.legalFormsOptions.length > EMPTY_ARRAY_LENGTH &&
            this.currentArTemplate != null
        ) {
            this.setSavedLegalFormsValue();
        }
        if (
            changes['countriesOptions'] != null &&
            this.countries.length > EMPTY_ARRAY_LENGTH &&
            this.currentArTemplate == null
        ) {
            this.formGroup?.controls['countries'].setValue([this.defaultCountry]);
        }
        if (
            changes['countriesOptions'] != null &&
            this.countries.length > EMPTY_ARRAY_LENGTH &&
            this.currentArTemplate != null
        ) {
            this.setSavedCountriesValue();
        }
        if (
            changes['localesOptions'] != null &&
            this.localesOptions.length > EMPTY_ARRAY_LENGTH &&
            this.currentArTemplate != null
        ) {
            this.setSaveLocalesValue();
        }

        if (changes['currentArTemplate']?.currentValue != null) {
            this.handleEditArTemplate();
        }
    }

    private initializeForm(): void {
        this.formGroup = new UntypedFormGroup({
            name: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
                validateNoEmptyField(),
            ]),
            caption: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
                validateNoEmptyField(),
            ]),
            state: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
            ]),
            consistency: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
            ]),
            countries: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
            ]),
            legalForms: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
            ]),
            businessModel: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
            ]),
            accountingBasis: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
            ]),
            locales: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
            ]),
            annualReportSteps: new UntypedFormControl(null),
        });

        const defaultState = this.stateOptions.find((state) => state.name === AnnualReportTemplateState.DRAFT);
        const defaultConsistency = this.consistencyOptions.find(
            (consistency) => consistency.name === AnnualReportTemplateConsistency.INCOMPLETE,
        );
        const defaultBusinessModel = this.businessModelsOptions.find(
            (businessModel) => businessModel.id === AnnualReportBusinessModel.OR,
        );
        this.formGroup?.controls['state'].setValue(defaultState);
        this.formGroup?.controls['consistency'].setValue(defaultConsistency);
        this.formGroup?.controls['businessModel'].setValue(defaultBusinessModel);
    }

    private monitorFormChanges(): void {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined.');
        }
        this.formGroup.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe(() => this.handleFormChanges());

        this.formGroup.statusChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), distinctUntilChanged(), untilDestroyed(this))
            .subscribe((statusChange) => this.formStatusChanged.emit(statusChange));
    }

    private handleFormChanges(): void {
        const name = this.formGroup?.get('name')?.value;
        const captionValue = this.formGroup?.get('caption')?.value;
        const legalForms: INameId[] = this.formGroup?.get('legalForms')?.value;
        const accountingBases: INameId[] = this.formGroup?.get('accountingBasis')?.value;
        const countries: INameId[] = this.formGroup?.get('countries')?.value;
        const state = this.formGroup?.get('state')?.value;
        const consistency = this.formGroup?.get('consistency')?.value;
        const businessModel = this.formGroup?.get('businessModel')?.value;
        const locales = this.formGroup?.get('locales')?.value;

        const legalFormsIds =
            legalForms != null && legalForms.length > EMPTY_ARRAY_LENGTH
                ? legalForms.map((legalForm) => legalForm?.id)
                : [];
        const accountingBasesIds =
            accountingBases != null && accountingBases.length > EMPTY_ARRAY_LENGTH
                ? accountingBases.map((accountingBasis) => accountingBasis?.id)
                : [];
        const countriesIds =
            countries != null && countries.length > EMPTY_ARRAY_LENGTH ? countries.map((country) => country?.id) : [];
        const localesIds =
            locales != null && locales.length > EMPTY_ARRAY_LENGTH ? locales.map((locale) => locale?.id) : [];

        this.arTemplateToBeSaved = {
            id: this.currentArTemplate?.id,
            name: name?.trim(),
            caption: { [this.i18NextService.language]: captionValue },
            legalFormsIds: legalFormsIds,
            accountingBasesIds: accountingBasesIds,
            countryISO31661Alpha2Codes: countriesIds,
            state: state?.name,
            annualReportSteps: this.annualReportSteps,
            consistency: consistency?.name,
            businessModel: businessModel?.id,
            locales: localesIds,
        };
        this.generalInformationChanged.emit(cloneDeep<IAnnualReportTemplateDTO>(this.arTemplateToBeSaved));
    }

    private handleStateOptions(): void {
        this.stateOptions.push({ id: AnnualReportTemplateState.DRAFT, name: AnnualReportTemplateState.DRAFT });
        this.stateOptions.push({ id: AnnualReportTemplateState.IN_REVIEW, name: AnnualReportTemplateState.IN_REVIEW });
        this.stateOptions.push({ id: AnnualReportTemplateState.PUBLISHED, name: AnnualReportTemplateState.PUBLISHED });
    }

    private handleConsistencyOptions(): void {
        this.consistencyOptions.push({
            id: AnnualReportTemplateConsistency.COMPLETE,
            name: AnnualReportTemplateConsistency.COMPLETE,
        });
        this.consistencyOptions.push({
            id: AnnualReportTemplateConsistency.INCOMPLETE,
            name: AnnualReportTemplateConsistency.INCOMPLETE,
        });
    }

    private handleBusinessModelsOptions(): void {
        this.businessModelsOptions.push({
            id: AnnualReportBusinessModel.OR,
            name: AnnualReportBusinessModel.OR,
        });
        this.businessModelsOptions.push({
            id: AnnualReportBusinessModel.HGB,
            name: AnnualReportBusinessModel.HGB,
        });
    }

    private handleEditArTemplate(): void {
        const currentArTemplateState = this.stateOptions.find((state) => state.name === this.currentArTemplate.state);
        const currentArTemplateConsistency = this.consistencyOptions.find(
            (consistency) => consistency.name === this.currentArTemplate.consistency,
        );
        const currentArTemplateBusinessModels = this.businessModelsOptions.find(
            (businessModel) => businessModel.id === this.currentArTemplate.businessModel,
        );
        this.formGroup?.controls['name'].setValue(this.currentArTemplate.name);
        this.formGroup?.controls['caption'].setValue(this.currentArTemplate.caption[this.i18NextService.language]);
        this.formGroup?.controls['state'].setValue(currentArTemplateState);
        this.formGroup?.controls['consistency'].setValue(currentArTemplateConsistency);
        this.formGroup?.controls['businessModel'].setValue(currentArTemplateBusinessModels);
        this.setSavedCountriesValue();
        this.setSavedLegalFormsValue();
        this.setSavedAccountingBasesValue();
        this.setSaveLocalesValue();
        this.annualReportSteps = this.currentArTemplate.annualReportSteps;
        this.getAnnualReportSavedDragAndDropItems(this.annualReportSteps);
    }

    private getAnnualReportSavedDragAndDropItems(savedOrder: AnnualReportStep[]): void {
        const savedOrderDragAndDropItems: IAumicoDragDropItem[] = [];

        savedOrder.forEach((step) => {
            let stepDescriptionKey: string | undefined;
            let fixed = false;
            switch (step) {
                case AnnualReportStep.GENERAL_INFORMATION:
                    stepDescriptionKey = GENERAL_INFORMATION_KEY;
                    fixed = true;
                    break;
                case AnnualReportStep.IMPORT_BALANCE_INCOME:
                    stepDescriptionKey = IMPORT_BALANCE_INCOME_KEY;
                    break;
                case AnnualReportStep.BALANCE_ACCOUNT_PROOF:
                    stepDescriptionKey = BALANCE_ACCOUNT_PROOF_KEY;
                    break;
                case AnnualReportStep.INCOME_ACCOUNT_PROOF:
                    stepDescriptionKey = INCOME_ACCOUNT_PROOF_KEY;
                    break;
                case AnnualReportStep.CFS:
                    stepDescriptionKey = CFS_KEY;
                    break;
                case AnnualReportStep.NOTES:
                    stepDescriptionKey = NOTES_KEY;
                    break;
                case AnnualReportStep.AOP:
                    stepDescriptionKey = AOP_KEY;
                    break;
                case AnnualReportStep.EMS:
                    stepDescriptionKey = EMS_KEY;
                    break;
                case AnnualReportStep.ADJUSTMENTS_RECLASSIFICATION:
                    stepDescriptionKey = ADJUSTMENTS_RECLASSIFICATION_KEY;
                    break;
                case AnnualReportStep.CR:
                    stepDescriptionKey = CR_KEY;
                    break;
                case AnnualReportStep.PRINT:
                    stepDescriptionKey = PRINT_KEY;
                    fixed = true;
                    break;
                case AnnualReportStep.FINANCIAL_CLOSING_PROCESS:
                    stepDescriptionKey = FINANCIAL_CLOSING_PROCESS_KEY;
                    break;
            }
            savedOrderDragAndDropItems.push({
                id: step,
                descriptionKey: stepDescriptionKey,
                fixed: fixed,
            });
        });

        this.annualReportSavedDragAndDropItems = savedOrderDragAndDropItems;
    }

    private setSavedAccountingBasesValue(): void {
        const selectedAccountingBases = this.getSelectedAccountingBases();
        this.formGroup?.controls['accountingBasis'].setValue(selectedAccountingBases);
    }

    private setSavedLegalFormsValue(): void {
        const selectedLegalForms = this.getSelectedLegalForms();
        this.formGroup?.controls['legalForms'].setValue(selectedLegalForms);
    }

    private setSavedCountriesValue(): void {
        const selectedCountries = this.getSelectedCountries();
        this.formGroup?.controls['countries'].setValue(selectedCountries);
    }
    private setSaveLocalesValue(): void {
        const selectedLocales = this.getSelectedLocales();
        this.formGroup?.controls['locales'].setValue(selectedLocales);
    }

    private getSelectedLegalForms(): INameId[] {
        const legalFormsSelectedOptions: INameId[] = [];

        this.currentArTemplate?.legalFormsIds.forEach((legalForm) => {
            const legalFormObject = this.legalFormsOptions.find((legalFormOption) => legalFormOption.id === legalForm);
            if (legalFormObject != null) {
                legalFormsSelectedOptions.push(legalFormObject);
            }
        });
        return legalFormsSelectedOptions;
    }
    private getSelectedLocales(): INameId[] {
        const localesOptions: INameId[] = [];
        this.currentArTemplate?.locales.forEach((locale) => {
            const localeObject = this.localesOptions.find(
                (localeOption) => localeOption.id.toLocaleLowerCase() === locale.toLocaleLowerCase(),
            );
            if (localeObject != null) {
                localesOptions.push(localeObject);
            }
        });
        return localesOptions;
    }
    private getSelectedAccountingBases(): INameId[] {
        const accountingBasesSelectedOptions: INameId[] = [];

        this.currentArTemplate?.accountingBasesIds.forEach((accountingBasis) => {
            const accountingBasisObject = this.accountingBasesOptions.find(
                (accountingBasisOption) => accountingBasisOption.id === accountingBasis,
            );
            if (accountingBasisObject != null) {
                accountingBasesSelectedOptions.push(accountingBasisObject);
            }
        });
        return accountingBasesSelectedOptions;
    }

    private getSelectedCountries(): INameId[] {
        const countriesSelectedOptions: INameId[] = [];
        this.currentArTemplate?.countryISO31661Alpha2Codes.forEach((country) => {
            const countryObject = this.countries.find((countryOption) => countryOption.id === country);
            if (countryObject != null) {
                countriesSelectedOptions.push(countryObject);
            }
        });
        return countriesSelectedOptions;
    }

    public onReportStepsChanged(annualReportSteps: IAumicoDragDropItem[]): void {
        this.annualReportSteps = annualReportSteps.map((annualReportStep) => annualReportStep.id);
        this.formGroup.controls['annualReportSteps'].setValue(this.annualReportSteps);
    }
}
